import React, { useEffect, useRef } from 'react';
import { useDialog } from '../context/DialogContext';
import ConfirmDialog from './ConfirmDialog';
import AlertDialog from './AlertDialog';
import HelpDialog from './HelpDialog';
import PromptDialog from './PromptDialog';

const DialogManager: React.FC = () => {
  const { dialogState, closeDialog } = useDialog();
  // ConfirmDialog calls onClose after onConfirm; only the first outcome counts.
  const settled = useRef(false);

  useEffect(() => {
    if (dialogState.isOpen) settled.current = false;
  }, [dialogState]);

  if (!dialogState.isOpen) return null;

  const settle = (callback?: () => void) => {
    if (settled.current) return;
    settled.current = true;
    // Close first so a callback can open the next dialog.
    closeDialog();
    callback?.();
  };

  const handleConfirm = () => settle(dialogState.onConfirm);
  const handleCancel = () => settle(dialogState.onCancel);

  switch (dialogState.type) {
    case 'confirm':
      return (
        <ConfirmDialog
          isOpen={dialogState.isOpen}
          onClose={handleCancel}
          onConfirm={handleConfirm}
          title={dialogState.title}
          message={dialogState.message || ''}
          confirmText={dialogState.confirmText}
          cancelText={dialogState.cancelText}
          variant={dialogState.variant}
        />
      );

    case 'alert':
      return (
        <AlertDialog
          isOpen={dialogState.isOpen}
          onClose={handleCancel}
          title={dialogState.title}
          message={dialogState.message || ''}
          okText={dialogState.okText}
          variant={dialogState.variant}
        />
      );

    case 'prompt':
      return (
        <PromptDialog
          isOpen={dialogState.isOpen}
          onClose={handleCancel}
          onSubmit={(value) => settle(() => dialogState.onSubmit?.(value))}
          title={dialogState.title}
          message={dialogState.message || ''}
          placeholder={dialogState.placeholder}
          defaultValue={dialogState.defaultValue}
          confirmText={dialogState.confirmText}
          cancelText={dialogState.cancelText}
          variant={dialogState.variant}
        />
      );

    case 'help':
      return (
        <HelpDialog
          isOpen={dialogState.isOpen}
          onClose={handleCancel}
          title={dialogState.title}
          content={dialogState.content || ''}
          maxWidth={dialogState.maxWidth}
          maxHeight={dialogState.maxHeight}
        />
      );

    default:
      return null;
  }
};

export default DialogManager; 